/**
 * Calculates the distance between two geographic coordinates using the Haversine formula
 * This is a more accurate method than simple Euclidean distance for GPS coordinates
 *
 * @param lat1 - Latitude of first point
 * @param lon1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lon2 - Longitude of second point
 * @returns Distance in kilometers
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return Math.round(distance * 1000) / 1000; // Round to 3 decimals
}

/**
 * Calculates the total distance traveled from an array of GPS positions
 * Sums up the distances between consecutive points
 *
 * @param positions - Array of position objects with latitude and longitude
 * @returns Total distance in kilometers
 */
export function calculateTotalDistance(
  positions: Array<{ latitude: number; longitude: number }>
): number {
  if (positions.length < 2) {
    return 0;
  }

  let totalDistance = 0;

  for (let i = 0; i < positions.length - 1; i++) {
    const current = positions[i];
    const next = positions[i + 1];

    const distance = calculateDistance(
      current.latitude,
      current.longitude,
      next.latitude,
      next.longitude
    );

    totalDistance += distance;
  }

  return Math.round(totalDistance * 100) / 100; // Round to 2 decimals
}

/**
 * Converts degrees to radians
 *
 * @param degrees - Value in degrees
 * @returns Value in radians
 */
function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}
